import * as React from "react";
import styled from "react-emotion";
import {Control, Field, Icon, Label, Select, TextArea} from "bloomer";
import Card from "../../../elements/Card";
import Button from "../../../elements/Button";
import {getGrammarCheckingResult} from "../../../api/models/GrammarChecker";
import getWordsCount from "../../../utils/WordsCount";
import SendGAEvent, {GAEventActions, GAEventModels} from "../../../utils/SendGAEvent";
import WordsLabel from "../../../elements/WordsLabel";
import ErrorUtils from "../../../utils/ErrorUtils";
import ModelContentContainer from "../../../elements/ModelContentContainer";
import SectionName from "../../../elements/SectionName";
import Spacer from "../../../elements/Spacer";
import {toast} from "react-toastify";
import InputWarning from "../../../elements/InputWarning";
import FieldDescription from "../../../elements/FieldDescription";
import {INVALID_FIELD_STYLE} from "../../../utils/Constants";

const InformationCard = styled("div")`
  border: 1px solid lightgrey;
  border-radius: 5px;
  padding: 10px;
  font-size: 16px;
  min-height: 30px;
  word-wrap: anywhere;
`;

const NormalSpan = styled("span")`
  line-height: 36px;
`;

interface IGrammarCheckerModelState {
    loading: boolean;
    text: string;
    textInputEmpty: boolean;
    languageCode: string;
    wordsCount: number;
    showLimitWarning: boolean;
    result: any;
    message: string;
    replacements: string;
    errorMessage: string;
}

class GrammarCheckerModel extends React.Component<{}, IGrammarCheckerModelState> {
    public WORDS_LIMIT = 1000;
    public languages = [{
        code: "en-US",
        name: "English (US)"
    }, {
        code: "en-GB",
        name: "English (GB)"
    }];

    constructor(props: {}) {
        super(props);

        this.state = {
            loading: false,
            text: "",
            textInputEmpty: false,
            languageCode: "en-US",
            wordsCount: 0,
            showLimitWarning: false,
            result: [],
            message: "",
            replacements: "",
            errorMessage: ""
        };
    }

    public componentDidMount() {
        SendGAEvent(GAEventActions.VIEW_MODEL, GAEventModels.GRAMMAR_BOT);
    }

    public updateWordsCount = async () => {
        const wordsCount = getWordsCount(this.state.text);
        this.setState({
            wordsCount,
            showLimitWarning: wordsCount > this.WORDS_LIMIT
        });
    };

    public handleLanguageCodeChange = (e: any) => {
        this.setState({
            languageCode: e.target.value
        });
    };

    public handleInputChange = (e: any) => {
        this.setState({
            text: e.target.value,
            textInputEmpty: false,
            errorMessage: ""
        }, () => {
            this.updateWordsCount();
        });
    };

    public onSubmit = async (e: any) => {
        e.target.blur();

        if (!this.state.text.trim() || this.state.loading || this.state.showLimitWarning) {

            this.setState({result: []});

            if (!this.state.text.trim()) {
                this.setState({
                    textInputEmpty: true,
                    showLimitWarning: false
                });
            }
            return;
        }

        this.setState({loading: true, result: [], errorMessage: ""});

        let errorOccurred = false;

        const response = await getGrammarCheckingResult(this.state.text.trim(), this.state.languageCode).catch((error) => {
            if (ErrorUtils.requestLimitExceeded(error.response)) {
                this.setState({
                    loading: false
                });
                toast.error(ErrorUtils.messageContent.REQUEST_LIMIT_EXCEEDED);
            } else {
                this.setState({
                    loading: false,
                    errorMessage: "Unable to check."
                });
            }
            errorOccurred = true;
        });

        if (errorOccurred) {
            return;
        }

        let result: any = [];
        const text = this.state.text.trim();

        if (response && response.data && response.data.matches) {
            let lastIndex = 0;
            const matches = response.data.matches;
            matches.forEach((error: any) => {
                const word = text.substr(error.offset, error.length);
                const leftPart = text.substring(lastIndex, error.offset);
                lastIndex = error.offset + error.length;
                result.push({
                    type: 'text',
                    text: leftPart
                }, {
                    type: 'error',
                    text: word,
                    message: error.message,
                    replacements: error.replacements.map((item: any) => item.value).join(', ')
                });
            });

            result.push({
                type: 'text',
                text: text.substring(lastIndex, text.length)
            });

            result = result.filter((item: any) => {
                return item.text;
            });

            this.setState({
                loading: false,
                result
            });
        } else {
            this.setState({
                loading: false,
                errorMessage: "Unable to check."
            });
        }

        SendGAEvent(GAEventActions.CALL_MODEL, GAEventModels.GRAMMAR_BOT);
    };

    public resetState = async (e: any) => {
        e.target.blur();

        this.setState({
            loading: false,
            text: "",
            textInputEmpty: false,
            wordsCount: 0,
            showLimitWarning: false,
            result: [],
            message: "",
            replacements: "",
            errorMessage: ""
        });
    };

    public render() {
        return (
            <ModelContentContainer>
                <Card>
                    <Field>
                        <Label>Language:</Label>
                        <Control>
                            <Select
                                onChange={this.handleLanguageCodeChange}
                                value={this.state.languageCode}
                            >
                                {
                                    this.languages.map((lang: any) => {
                                        return <option key={lang.code} value={lang.code}>{lang.name}</option>
                                    })
                                }
                            </Select>
                        </Control>
                    </Field>
                    <Field>
                        <Control>
                            <SectionName>Text:</SectionName>
                            <TextArea
                                style={(this.state.textInputEmpty || this.state.showLimitWarning) ? INVALID_FIELD_STYLE : undefined}
                                type="text"
                                placeholder="Enter your text here"
                                value={this.state.text}
                                onChange={this.handleInputChange}
                                onPaste={this.handleInputChange}
                            />
                            <div>
                                {
                                    this.state.textInputEmpty ?
                                        <InputWarning>Please input a text.</InputWarning> : <></>
                                }
                                {
                                    this.state.showLimitWarning ? <InputWarning>
                                        Input size limit has been exceeded. Please decrease the size.
                                    </InputWarning> : <></>
                                }
                                <WordsLabel>{this.state.wordsCount}{" "}{this.state.wordsCount > 1 ? "words" : "word"}</WordsLabel>
                            </div>
                        </Control>
                    </Field>
                    <div>
                        <Button onClick={this.onSubmit}>Submit</Button>
                        <Button onClick={this.resetState} style={{marginLeft: "5px"}}>Reset</Button>
                    </div>
                    {
                        this.state.loading ? <div>
                            <Spacer/>
                            <span>Loading...</span>
                            <Icon className="fas fa-spinner fa-spin"/>
                        </div> : <></>
                    }
                    {
                        this.state.result.length && !this.state.loading ? <div>
                            <Spacer/>
                            <SectionName>Output:</SectionName>
                            <FieldDescription>Please click on the word to view details.</FieldDescription>
                            <InformationCard>
                                {
                                    this.state.result.map((item: any) => {
                                        if (item.type === 'error') {
                                            return <span
                                                onClick={() => {
                                                    this.setState({
                                                        message: item.message,
                                                        replacements: item.replacements
                                                    });
                                                }}
                                                title={item.error}
                                                style={{
                                                    cursor: 'pointer',
                                                    textDecoration: 'underline',
                                                    textDecorationColor: 'red',
                                                    color: 'red'
                                                }}>{item.text}</span>;
                                        } else {
                                            return <NormalSpan>{item.text}</NormalSpan>;
                                        }
                                    })
                                }
                            </InformationCard>
                            <Spacer/>
                            {
                                this.state.message || this.state.replacements ? <InformationCard>
                                    <div>
                                        <b>Message: </b>{this.state.message}
                                    </div>
                                    <div>
                                        <b>Replacements: </b>{this.state.replacements}.
                                    </div>
                                </InformationCard> : <></>
                            }
                        </div> : <></>
                    }
                    {
                        this.state.errorMessage && !this.state.loading ? <div>
                            <Spacer/>
                            <InformationCard>{this.state.errorMessage}</InformationCard>
                        </div> : <></>
                    }
                </Card>
            </ModelContentContainer>
        );
    }
}

export default GrammarCheckerModel;


// WEBPACK FOOTER //
// ./src/routes/Dashboard/GrammarChecker/GrammarCheckerModel.tsx